import {
  MiniCart,
  SlideOverDialog,
} from "@bluebottlecoffee/design-system/components";
import {
  GiftCardDetails,
  GiftCardFormCopy,
} from "@bluebottlecoffee/design-system/components/GiftCard/GiftCardForm";
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  useAnalytics,
  useCart,
  useCheckout,
} from "@chordcommerce/react-autonomy";
import { Currency } from "dinero.js";
import {
  CartLineItem,
  Result,
  SubscriptionInterval,
} from "@bluebottlecoffee/design-system/components/lib/types";
import { trackEvent } from "../lib/segment/track-event";
import useVariantPrice from "../lib/chord/hooks/components/use-variant-price";
import { isFeatureEnabled } from "../lib/utils/is-feature-enabled";
import { AlgoliaRecommendProduct } from "../lib/algolia/types";
import {
  SourceProductRec,
  transformProductRecsToShopCards,
} from "../lib/transformers/product-recs";
import { Cart, CartBaseProps, SharedQueryKey } from "../lib/sanity/shared";
import {
  continueToCheckout,
  toCartLineItemsProps,
  toFreeShippingBarProps,
  toGiftMessageFormProps,
  toLineItemsProps,
  toOrderSummaryProps,
  toProductRecsCopyProps,
  toProductRecsProps,
  toRemovePromoAlertProps,
} from "../lib/transformers/cart";
import { toMiniCartProps } from "../lib/transformers/mini-cart";
import {
  cartVariants,
  discountTotal,
  getPromoLabels,
  getPromoWithCode,
  isFreeStandardShipping,
  shouldRenderFreeShippingBar,
} from "../lib/utils";
import emailIsValid from "../lib/utils/strings/email-is-valid";
import {
  getRecommendedProducts,
  productRecsWithBestValueVariant,
} from "../lib/utils/product-recs/product-recs";
import { prepaidSubOptions } from "../lib/chord/services/oms/prepaid-sub-options";

export interface DialogData extends Pick<
  CartBaseProps,
  | "lang"
  | "region"
  | typeof SharedQueryKey.SUBSCRIBABLE_PRODUCTS
  | SharedQueryKey.PRODUCT_RECS
  | SharedQueryKey.NAV_AND_CART_INFO_BANNER
> {
  miniCartCopy: Cart;
  giftCardFormCopy: GiftCardFormCopy;
}

function validate(data: Cart) {
  if (!data.dialog.heading) {
    throw Error("MiniCart must have a heading.");
  }
}

export const MiniCartDialog: FunctionComponent<DialogData> = ({
  miniCartCopy,
  giftCardFormCopy,
  subscribableProducts,
  navAndCartInfoBanner,
  productRecs: staticProductRecs,
  ...defaultProps
}) => {
  const { lang, region } = defaultProps;
  const [promoSuccess, setPromoSuccess] = useState<boolean>(true);
  const [promoErrorMessage, setPromoErrorMessage] = useState<string>(
    miniCartCopy.orderSummary.promoCopy.errorMessage[lang],
  );

  const {
    cart,
    loadCart,
    isFetching,
    isLoaded,
    addToCart,
    addSubscription,
    removeFromCart,
    modifyLineItem,
    addPromoCode,
    removePromoCode,
    modifyCart,
    modifyGiftCards,
  } = useCart();
  const { prepareCheckout } = useCheckout();

  const { trackCartViewed, trackProductClicked } = useAnalytics();
  const [loadingCart, setLoadingCart] = useState<boolean>(!isLoaded);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const [productRecs, setProductRecs] = useState<
    SourceProductRec[] | AlgoliaRecommendProduct[]
  >(staticProductRecs.slice(0, 3));
  const [cartLoaded, setCartLoaded] = useState(false);

  useEffect(() => {
    loadCart()
      .then(() => {
        setCartLoaded(true);
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error(error.message);
      });
  }, [loadCart]);

  useEffect(() => {
    if (isFetching) {
      setLoadingCart(true);
    } else {
      setLoadingCart(false);
    }
  }, [isFetching]);

  const handleModifyQuantity = useCallback(
    async (lineItemId, quantity) => {
      setLoadingCart(true);
      await modifyLineItem({
        lineItemId,
        attributes: { quantity: Math.max(quantity, 1) },
      });
      setLoadingCart(false);
    },
    [modifyLineItem],
  );

  const handleModifyGiftCards = async (options: {
    giftCardsDetails: GiftCardDetails[];
  }) => {
    await modifyGiftCards({
      giftCards: {
        giftCardsDetails: options.giftCardsDetails.map((detail) => ({
          id: detail.id.toString(),
          recipientEmail: detail.recipientEmail,
          recipientName: detail.recipientName,
          giftMessage: detail.giftMessage,
          purchaserName: detail.purchaserName,
          sendEmailAt: detail.sendEmailAt,
        })),
      },
    });
  };

  const handleModifyCart = useCallback(
    async (lineItem: CartLineItem, intervalId) => {
      setLoadingCart(true);

      const {
        variant: { sku, subscriptionOptions, name },
        id,
        quantity,
        prePaidSubscription,
      } = lineItem;

      await removeFromCart({ lineItemId: id });

      const { options, intervals } = subscriptionOptions;

      const { length, unit, _id } = intervals.find(
        (interval) => interval._id === intervalId,
      );

      const subscriptionQuantity =
        options?.find((opt) => opt.quantityLabel.quantity === quantity) ??
        options[options.length - 1];

      try {
        if (_id === "one-time-purchase") {
          await addToCart({
            sku,
            quantity,
          });

          trackEvent("cart_one_time_change", {
            name,
            quantity,
            sku,
          });
        } else {
          if (prePaidSubscription) {
            await addToCart({
              sku,
              quantity,
              // @ts-ignore Chord type mismatch
              options: prepaidSubOptions({
                sku,
                interval: { length, unit },
                installmentCount: prePaidSubscription.installmentCount,
              }),
            });
          } else {
            await addSubscription({
              sku,
              quantity: subscriptionQuantity.quantityLabel.quantity,
              interval: {
                length,
                unit,
              },
              endDate: null,
            });
          }

          trackEvent("cart_subscription_change", {
            name,
            sku,
            quantity: subscriptionQuantity.quantityLabel.quantity,
            interval: {
              length,
              unit,
            },
          });
        }
      } catch (e) {
        loadCart();
        setLoadingCart(false);
        console.error(e);
      } finally {
        setLoadingCart(false);
      }
    },
    [modifyCart],
  );

  validate(miniCartCopy);

  useEffect(() => {
    loadCart().catch((error) => {
      console.error(error.message);
    });
  }, [loadCart]);

  const handleRemoveFromCart = async (lineItemId: number) => {
    setLoadingCart(true);
    try {
      await removeFromCart({ lineItemId });
      setLoadingCart(false);
      return {
        success: true,
        message: miniCartCopy.removeSuccessMessage[lang],
      };
    } catch (error) {
      setLoadingCart(false);
      console.error(error.message);
      return {
        success: false,
        message: error.message,
      };
    }
  };

  const onAddPromoCodeSubmit = async (promoCode: string) => {
    try {
      setLoadingCart(true);
      await addPromoCode({ promoCode });
      setLoadingCart(false);
      setPromoSuccess(true);
      setPromoErrorMessage("");
    } catch (error) {
      setPromoSuccess(false);
      setPromoErrorMessage(error.message);
      console.error(error);
    }
  };

  const handleRemovePromoCode = async (promoCode: string) => {
    try {
      setLoadingCart(true);
      await removePromoCode({ promoCode });
      setLoadingCart(false);
      setPromoSuccess(true);
      setPromoErrorMessage("");
    } catch (error) {
      console.error(error);
    }
  };

  const onRemoveGiftMessage = async () => {
    try {
      await modifyCart({
        attributes: {
          // @ts-ignore
          giftNoteAttributes: {
            from: "",
            recipient: "",
            note: "",
          },
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const onAddGiftMessage = async (
    to: string,
    from: string,
    giftMessage?: string,
  ) => {
    try {
      await modifyCart({
        attributes: {
          // @ts-ignore
          giftNoteAttributes: {
            from,
            recipient: to,
            note: giftMessage,
          },
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const transformedGiftNote =
    cart.giftNote?.from && cart.giftNote.recipient
      ? {
          to: cart.giftNote.recipient,
          from: cart.giftNote.from,
          giftMessage: cart.giftNote.note,
        }
      : null;

  const miniCartProps = toMiniCartProps(
    miniCartCopy,
    giftCardFormCopy,
    region,
    lang,
  );

  function isCartEmpty(): boolean {
    return cart.lineItems?.length === 0;
  }

  useEffect(() => {
    const recommendEnabled = isFeatureEnabled(
      process.env.NEXT_PUBLIC_ENABLE_ALGOLIA_RECOMMEND,
    );
    if (recommendEnabled && cartLoaded && !isCartEmpty()) {
      getRecommendedProducts({
        cart,
        lang,
        setProductRecs,
      });
    }
  }, [cartLoaded, cart.lineItems]);

  const productRecsProps = useMemo(
    () =>
      toProductRecsProps(productRecs, region, lang, cart.currency as Currency),
    [cart.currency, lang, productRecs, region],
  );

  const handleAddToCardProductRecs = async (
    sku: string,
    quantity: number,
    interval: SubscriptionInterval,
  ): Promise<Result> => {
    try {
      if (interval) {
        const data = await addSubscription({
          sku,
          quantity,
          interval,
          endDate: null,
        });

        trackEvent("cart_you_may_also_like_add", {
          name: data.lineItems[0].variant.name,
          quantity,
          sku,
          interval,
        });
      } else {
        const data = await addToCart({ sku, quantity });

        trackEvent("cart_you_may_also_like_add", {
          name: data.lineItems[0].variant.name,
          quantity,
          sku,
        });
      }
      return {
        success: true,
        message: "Success.",
      };
    } catch (e) {
      loadCart();
      return {
        success: false,
        message: e?.message ?? "Sorry, something went wrong.",
      };
    }
  };

  return (
    <SlideOverDialog
      contentType="minicart"
      showDismissButton={false}
      overflowY={isCartEmpty()}
      dismissText={miniCartCopy.dialog.dismissText[lang]}
      onOpen={trackCartViewed}
    >
      <MiniCart
        {...miniCartProps}
        showReturnToShopButton
        showHeading
        cartProps={{
          ...miniCartProps.cartCopy,
          cartLineItemsProps: {
            ...toCartLineItemsProps(miniCartCopy, lang, giftCardFormCopy),
            removeFromCart: handleRemoveFromCart,
            modifyQuantity: handleModifyQuantity,
            modifyCart: handleModifyCart,
            cartLineItems: cartVariants({
              data: cart,
              dialect: { region, lang },
              subscribableProducts,
              oneTimeLabel: miniCartCopy.oneTimePurchase[lang],
              prepayShipmentsLabel: miniCartCopy.prepayShipmentsLabel[lang],
            }),
            cartView: "mini",
            modifyGiftCards: handleModifyGiftCards,
            emailIsValid,
            loadingCart,
            useVariantPrice,
          },
          infoBanner:
            navAndCartInfoBanner?.showInfoBanners &&
            navAndCartInfoBanner.text[lang],
          renderFreeShippingBar: shouldRenderFreeShippingBar(cart),
          freeShippingBarProps: {
            ...toFreeShippingBarProps(miniCartCopy.freeShippingBar, lang),
            estimatedTotal: {
              amount: Math.round(
                (Number(cart.itemTotal) + Number(cart.adjustmentTotal)) * 100,
              ),
              currency: cart.currency as Currency,
            },
            freeShipPromoCode: isFreeStandardShipping(cart),
          },
          orderSummaryProps: {
            ...toOrderSummaryProps(miniCartCopy.orderSummary, lang),
            bgColor: miniCartCopy.dialog.bgColor,
            cartView: "mini",
            lineItems: {
              ...toLineItemsProps(miniCartCopy.orderSummary.lineItems, lang),
              credit:
                Number(cart.totalApplicableStoreCredit) !== 0.0
                  ? cart.displayTotalApplicableStoreCredit
                  : undefined,
              currency: cart.currency as Currency,
              discountTotal: discountTotal(cart),
              estimatedTotalCost: cart.displayOrderTotalAfterStoreCredit
                ? cart.displayOrderTotalAfterStoreCredit
                : undefined,
              freeShipping: isFreeStandardShipping(cart),
              grossSubtotal: cart.displayItemTotal
                ? cart.displayItemTotal
                : miniCartCopy.orderSummary.lineItems.tbd[lang],
              promoLabels: getPromoLabels(cart),
              shippingCost:
                cart.shipTotal === "0.0" ? undefined : cart.displayShipTotal,
              variantsTotal: cart.totalQuantity
                ? cart.totalQuantity
                : undefined,
            },
            checkoutError,
            giftMessageForm: {
              ...toGiftMessageFormProps(
                miniCartCopy.orderSummary.giftMessageCopy,
                lang,
              ),
              giftMessageProp: transformedGiftNote,
              removeGiftMessage: onRemoveGiftMessage,
              addGiftMessage: onAddGiftMessage,
            },
            promoForm: {
              error: !promoSuccess,
              promoCode: getPromoWithCode(cart),
              promoCopy: {
                add: miniCartCopy.orderSummary.promoCopy.add[lang],
                apply: miniCartCopy.orderSummary.promoCopy.apply[lang],
                placeholder:
                  miniCartCopy.orderSummary.promoCopy.placeholder[lang],
                errorMessage: promoErrorMessage,
              },
              ...toRemovePromoAlertProps(
                miniCartCopy.orderSummary.promoCopy,
                lang,
              ),
              removePromoCode: handleRemovePromoCode,
              addPromoCode: onAddPromoCodeSubmit,
            },
            /* TODO
             * If the subscription agreement feature is ever requested, define
             * subsPresent to render the checkbox.
             */
            subsPresent: false,
            ctaClick: continueToCheckout({
              modifyCart,
              region,
              lang,
              // remove after checkout is fully migrated to embedded checkout
              prepareCheckout,
              onError: setCheckoutError,
            }),
          },
          productRecsProps: {
            addToCartClick: handleAddToCardProductRecs,
            emptyCart: isCartEmpty(),
            productRecs: productRecsProps,
            productRecsCopy: miniCartCopy.productRecsCopy[lang],
            productRecShopCards: transformProductRecsToShopCards(
              productRecsWithBestValueVariant(productRecs, lang),
              lang,
              region,
              (product) => trackProductClicked(product),
            ),
            ...toProductRecsCopyProps(miniCartCopy, lang),
          },
        }}
      />
    </SlideOverDialog>
  );
};
